import { makeObservable, observable, action, computed } from 'mobx';

/**
 * EntityDiscoveryStore — accumulates Plex entities surfaced by the
 * analyst's `fetch_*` tool calls during a streaming response. Purpose
 * is to convert dead-wait time (75-250s analyst inference + retrieval)
 * into productive exploration: as entities arrive, they appear in
 * the right-column Entities panel and the user can scan / click
 * before the full synthesis lands.
 *
 * Data flow:
 *   useEntityDiscovery (observer hook in ConversationView) walks
 *   client.turns + client.subAgentTurns, extracts entities from
 *   tool result JSON content blocks, and calls addEntity / addBatch.
 *
 * Dedup: by plex_id (one card per entity, even if multiple fetches
 * return the same id). Earliest discoveredAt wins; subsequent
 * sightings update the sightings counter + sourceToolUseIds set.
 *
 * Lifecycle: cleared per-chat by ProjectView's chatId-change effect
 * (mirrors SourceReferencesStore.clearSources pattern).
 */
class EntityDiscoveryStore {
  entities = [];          // observable.deep — array of EntityRow
  selectedEntityId = null;// plex_id of the entity the user clicked (or null)
  _idToIdx = new Map();   // plex_id → index in entities (O(1) dedup)
  nextRowId = 1;          // monotonic id for stable React keys

  constructor() {
    makeObservable(this, {
      entities: observable.deep,
      selectedEntityId: observable,
      addEntity: action,
      addBatch: action,
      clearEntities: action,
      selectEntity: action,
      removeByAgentId: action,
      count: computed,
      countByCategory: computed,
    });
  }

  /**
   * Select (toggle) an entity by object or plex_id. Clicking the selected
   * entity again clears the selection. Panels reflect it via a --selected
   * class; the observable lets other surfaces react to the focus.
   * Returns the resulting selectedEntityId.
   */
  selectEntity(entityOrId) {
    const id = typeof entityOrId === "string" ? entityOrId : entityOrId?.plex_id;
    if (!id) return this.selectedEntityId;
    this.selectedEntityId = this.selectedEntityId === id ? null : id;
    return this.selectedEntityId;
  }

  get count() {
    return this.entities.length;
  }

  /**
   * Group entities by category (target, compound, geneset, etc.) for
   * sectioned rendering in the panel. Returns { [category]: EntityRow[] }
   * with insertion-order preserved within each bucket.
   */
  get countByCategory() {
    const counts = {};
    for (const e of this.entities) {
      counts[e.category || 'other'] = (counts[e.category || 'other'] || 0) + 1;
    }
    return counts;
  }

  /**
   * Add a single entity. Dedupes by plex_id; on repeat, increments
   * `sightings` and merges sourceToolUseIds. Returns the row.
   */
  addEntity({
    plex_id,
    name,
    category,
    title = null,
    score = null,
    rank = null,
    gene_symbol = null,
    sourceToolUseId = null,
    sourceTool = null,
    turnIdx = null,
  }) {
    if (!plex_id) return null;
    const existingIdx = this._idToIdx.get(plex_id);
    if (existingIdx != null) {
      const row = this.entities[existingIdx];
      row.sightings = (row.sightings || 1) + 1;
      if (sourceToolUseId && !row.sourceToolUseIds.includes(sourceToolUseId)) {
        row.sourceToolUseIds.push(sourceToolUseId);
      }
      // Upgrade fields if a later fetch supplied richer metadata.
      if (name && (!row.name || row.name === row.plex_id)) row.name = name;
      if (title && (!row.title || title.length > row.title.length)) row.title = title;
      if (score != null && (row.score == null || score > row.score)) row.score = score;
      if (gene_symbol && !row.gene_symbol) row.gene_symbol = gene_symbol;
      return row;
    }
    const row = {
      id: `e${this.nextRowId++}`,
      plex_id,
      name: name || plex_id,
      category: category || 'other',
      title,
      score,
      rank,
      gene_symbol,
      discoveredAt: Date.now(),
      sightings: 1,
      sourceToolUseIds: sourceToolUseId ? [sourceToolUseId] : [],
      sourceTool,
      turnIdx,
    };
    this._idToIdx.set(plex_id, this.entities.length);
    this.entities.push(row);
    return row;
  }

  /**
   * Add many entities at once (a single fetch_results return). Each
   * item must have at least { plex_id }. Other fields optional.
   */
  addBatch(items, meta = {}) {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item) continue;
      this.addEntity({ ...item, ...meta });
    }
  }

  /**
   * Clear all entities. Called when chat changes.
   */
  clearEntities() {
    this.entities = [];
    this._idToIdx = new Map();
    this.nextRowId = 1;
    this.selectedEntityId = null;
  }

  /**
   * Drop entities sourced by a specific agentId (mirrors
   * SourceReferencesStore pattern for cancellation cleanup). Currently
   * unused but the hook is here for parity.
   */
  removeByAgentId(agentId) {
    if (!agentId) return;
    this.entities = this.entities.filter(
      (e) => !e.sourceToolUseIds.some((tid) => tid === agentId),
    );
    this._idToIdx = new Map();
    this.entities.forEach((e, i) => this._idToIdx.set(e.plex_id, i));
  }
}

export default EntityDiscoveryStore;
export const entityDiscoveryStore = new EntityDiscoveryStore();
